import { MoneyBox, SuccessIcon } from "asset/icons";
import React, { useMemo, useState } from "react";
import { Button } from "component/ui/Button";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getPublicFinance, actOnPublicFinance } from "api/credit-fetchers";
import AppModal from "../../../component/AppModal";
import { Information, DocumentText } from "iconsax-reactjs";
import { ExternalLink } from "lucide-react";
import Loader from "../../..//component/loader";
import { toast } from "react-toastify";
import { SmallTradevuLogo } from "../../../component/SmallTradevuLogo";

function formatAmount(amount, currency) {
    if (amount == null) return "—";
    const sym = { NGN: "₦", USD: "$", EUR: "€", GBP: "£" }[String(currency).toUpperCase()] || "";
    return `${sym} ${Number(amount).toLocaleString()}`;
}
function formatDate(d) {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt)
        ? "—"
        : dt.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
          });
}
function initials(name = "") {
    return (
        name
            .trim()
            .split(/\s+/)
            .map((n) => n[0])
            .slice(0, 2)
            .join("")
            .toUpperCase() || "—"
    );
}
function truncateFileName(fileName) {
  if (!fileName) return "swift-confirm... .pdf";
  const parts = fileName.split(".");
  if (parts.length < 2) return fileName.slice(0, 15) + "...";
  const ext = parts.pop();
  const base = parts.join(".");
  if (base.length > 13) {
    return `${base.slice(0, 13)}... .${ext}`;
  }
  return fileName;
}

const PreviewDomiciliation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState("");

    const [confirmDeclineOpen, setConfirmDeclineOpen] = useState(false);
    const [declinedOpen, setDeclinedOpen] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ["get-single-finance", id],
        queryFn: () => getPublicFinance({ financeId: id }),
        enabled: !!id,
    });

    const f = data ?? {};
    const supplier = f.businessName || "[Supplier Name]";
    const amountDisplay = formatAmount(f.amount, f.currency);
    const bankName = f.bank?.bankName || "[Bank name]";
    const bankAcctNo = f.bank?.accountNumber || "[Account number]";
    const bankAcctName = f.bank?.accountName || "[Account name]";
    const contractType = f.type || "[Invoice/Order]";
    const contractId = f.title || "[Invoice / Order ID]";
    const due = formatDate(f.dueDate);
  const requested = formatDate(f.RequestDate || f?.allData?.createdAt);
    const typeLabel = (f.type || "Invoice").toLowerCase();

  const isPaymentFinance = f.allData?.financeType === 'payment_finance';
  const swiftRef = f.allData?.paymentFinance?.swiftReference;
  const swiftType = swiftRef?.swiftType;

    const details = useMemo(
        () => [
            {
                key: "Supplier",
                render: (
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-[#F2F3FC] text-[#6B6F8C] grid place-items-center text-sm">
                            {initials(supplier)}
                        </div>
                        <span className="text-[#101828]">{supplier}</span>
                    </div>
                ),
            },
            { key: "Amount to be paid", value: amountDisplay },
            {
                key: "Domiciliation account",
                render: (
                    <div className="text-right">
                        <div className="text-[#101828]">{bankName}</div>
                        <div className="text-[#6B6F8C]">{bankAcctNo}</div>
                        <div className="text-[#6B6F8C]">{bankAcctName}</div>
                    </div>
                ),
            },
            { key: "Contract Type", value: contractType },
            { key: "Contract ID", value: contractId },
            { key: "Payment due", value: due },
        ],
        [supplier, amountDisplay, bankName, bankAcctNo, bankAcctName, contractType, contractId, due]
    );

    const handleDeclineConfirm = async () => {
        if (!id) return;
        try {
            setActionError("");
            setActionLoading(true);
            const res = await actOnPublicFinance({ financeId: id, status: "rejected" });
            if (res?.success === false) {
                const msg = res?.message || "Couldn’t decline request. Try again.";
                setActionError(msg);
                toast.error(msg);
                return;
            }
            setConfirmDeclineOpen(false);
            setDeclinedOpen(true);
        } catch (e) {
            const msg = e?.response?.data?.message || "Couldn’t decline request. Try again.";
            setActionError(msg);
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!id) return;
        try {
            setActionError("");
            setActionLoading(true);
            const res = await actOnPublicFinance({ financeId: id, status: "accepted" });
            if (res?.success !== false) {
                navigate("/accepted-domiciliation", { state: { supplier } });
            } else {
                const msg = res?.message || "Couldn’t accept request. Try again.";
                setActionError(msg);
                toast.error(msg);
            }
        } catch (e) {
            const msg = e?.response?.data?.message || "Couldn’t accept request. Try again.";
            setActionError(msg);
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="flex items-center w-full h-screen bg-bg-color">
            <section className="flex flex-col items-center w-11/12 h-full py-4 mx-auto text-center md:w-1/2">
                <MoneyBox />
                <h3 className="font-[450] text-gray-90 text-lg md:!text-3xl">
            {isPaymentFinance ? "Verify transaction" : "Domiciliation agreement"}
                </h3>

          {!isPaymentFinance ? (
            <>
              <p className="mb-8 text-sm text-center font-light md:!text-base text-gray-10">
                {supplier} has requested that payment of {amountDisplay} for <br />
                {contractId} be made to their Tradevu account.
                <br /> Please review details of the agreement and respond below.
              </p>

              <div className="w-10/12 p-6 my-4 bg-white border rounded-md !border-secondary-50">
                <div className="p-4 border rounded-md !border-secondary-50">
                  {isLoading ? (
                    <div className="space-y-3">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex justify-between items-center py-2">
                          <div className="h-4 w-40 bg-gray-100 animate-pulse rounded" />
                          <div className="h-4 w-56 bg-gray-100 animate-pulse rounded" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {details.map(({ key, value, render }) => (
                        <div
                          key={key}
                          className="flex items-center py-2 justify-between"
                        >
                          <p className="text-sm font-light text-left text-secondary-40">
                            {key}
                          </p>
                          {render ? (
                            render
                          ) : (
                            <p className="text-sm font-light text-right text-gray-90">
                              {value || "—"}
                            </p>
                          )}
                        </div>
                      ))}
                      <div className="border my-1" />
                      <div className="flex items-center py-2 justify-between">
                        <p className="text-sm font-light text-secondary-40">
                          Request date
                        </p>
                        <p className="text-sm font-light text-gray-90">{requested}</p>
                      </div>
                    </>
                  )}
                </div>

                {!!actionError && (
                  <p className="text-xs text-red-500 mt-2 text-right">{actionError}</p>
                )}

                <div className="flex items-center justify-end gap-6 pt-3">
                  <Button
                    className="py-1 text-danger-20 rounded-[8px]"
                    variant="ghost"
                    onClick={() => setConfirmDeclineOpen(true)}
                    disabled={actionLoading}
                  >
                    Decline
                  </Button>
                  <Button
                    onClick={handleAccept}
                    className="py-1 rounded-[8px]"
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Loader /> : "Accept request"}
                  </Button>
                </div>
              </div>

              <p className="flex items-center justify-center text-xs font-light gap-x-1 text-secondary-40">
                Powered by <SmallTradevuLogo /> | Terms and Privacy Policy
              </p>
            </>
          ) : (
            <>
              <p className="mb-8 text-sm text-center font-light md:!text-base text-[#4F5372]">
                {supplier} has requested you to verify that this <br /> transaction was successfully initiated and processed. Please  <br /> review the details and respond below.
              </p>

              <div className="w-full max-w-[640px] p-4 md:p-4 my-6 bg-white border border-[#EBEDF9] rounded-[24px] shadow-[0px_4px_24px_rgba(0,0,0,0.03)] text-left">
                {isLoading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex justify-between items-center py-3">
                        <div className="h-4 w-40 bg-gray-100 animate-pulse rounded" />
                        <div className="h-4 w-56 bg-gray-100 animate-pulse rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Customer row */}
                    <div className="flex items-center justify-between py-1">
                      <span className="text-[#6F739B] font-[350] text-sm">Customer</span>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-[#F2F3FC] text-[#6B6F8C] flex items-center justify-center text-sm font-semibold">
                          {initials(supplier)}
                        </div>
                        <span className="text-[#161827] font-[350] text-sm">{supplier}</span>
                      </div>
                    </div>

                    {/* Bank details row */}
                    <div className="flex items-start justify-between py-1">
                      <span className="text-[#6F739B] font-[350] text-sm">Bank details</span>
                      <div className="text-right space-y-1">
                        <div className="text-[#161827] font-[350] text-sm">
                          {swiftRef?.bankName || "—"}
                        </div>
                        <div className="text-[#6F739B] text-[14px]">
                          {swiftRef?.contactPerson || "—"}
                        </div>
                        <div className="text-[#6F739B] text-[14px]">
                          {swiftRef?.contactEmail || "—"}
                        </div>
                      </div>
                    </div>

                    {/* Document/UETR row */}
                    {swiftType === "UETR" ? (
                      <div className="flex items-center justify-between py-1">
                        <span className="text-[#6F739B] font-[350] text-sm">UETR</span>
                        <span className="text-[#161827] font-[350] text-sm select-all break-all text-right">
                              {swiftRef?.uetr || swiftRef?.UETR || "—"}
                        </span>
                      </div>
                    ) : (
                      swiftRef?.link?.url && (
                        <div className="flex items-center justify-between py-1">
                          <div className="flex items-center gap-2 text-[#6F00FF] font-medium text-[15px]">
                            <DocumentText size={20} className="text-[#6F00FF]" />
                            <span>SWIFT confirmation</span>
                          </div>
                          <a
                            href={swiftRef.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-[#6F00FF] hover:underline font-semibold text-[15px]"
                          >
                            {truncateFileName(swiftRef.link.name)}
                            <ExternalLink size={15} className="text-[#6F00FF]" />
                          </a>
                        </div>
                      )
                    )}

                    <div style={{ borderTop: '1.5px solid #EEF2F6' }} className=" my-4" ></div>

                    {/* Request date row */}
                    <div className="flex items-center justify-between pb-1">
                      <span className="text-[#6F739B] font-[350] text-sm">Request date</span>
                      <span className="text-[#161827] font-[350] text-sm">{requested}</span>
                    </div>
                  </div>
                )}

                {!!actionError && (
                  <p className="text-xs text-red-500 mt-3 text-right">{actionError}</p>
                )}

                <div className="flex items-center justify-end gap-6 mt-8">
                  <Button
                    className="text-[#DC2626] hover:text-[#B91C1C] hover:bg-transparent font-semibold text-[16px] transition-colors bg-transparent border-none outline-none cursor-pointer"
                    variant="ghost"
                    onClick={() => setConfirmDeclineOpen(true)}
                    disabled={actionLoading}
                  >
                    Decline
                  </Button>
                  <Button
                    onClick={handleAccept}
                    className="bg-[#6F00FF] hover:bg-[#5D00D6] text-white font-semibold text-[16px] px-8 py-3 rounded-[12px] h-12 shadow-[0px_4px_12px_rgba(111,0,255,0.15)] flex items-center justify-center min-w-[200px]"
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Loader /> : "Confirm transaction"}
                  </Button>
                </div>
              </div>

              <p className="flex items-center justify-center text-xs font-light gap-x-1 text-secondary-40 mt-4">
                Powered by <SmallTradevuLogo /> | Terms and Privacy Policy
              </p>
            </>
          )}
            </section>

            {/* Confirm decline */}
            <AppModal
                isOpen={confirmDeclineOpen}
                onClose={() => setConfirmDeclineOpen(false)}
                icon={<Information className="h-10 w-10 text-[#DB6704]" />}
                title="Are you sure?"
                body={
                    <>
                        Declining this may result in{" "}
                        <span className="font-medium text-[#383B56]">{supplier}</span> not receiving
                        the necessary financing to fulfill your{" "}
                        <span className="font-medium text-[#383B56]">
                      {isPaymentFinance ? "Payment" : typeLabel === "order" ? "Order" : "Invoice"}
                        </span>
                        .
                    </>
                }
                secondary={{
                    label: "Go back",
                    onClick: () => setConfirmDeclineOpen(false),
                    disabled: actionLoading,
                }}
                primary={{
                    label: "Decline request",
                    onClick: handleDeclineConfirm,
                    variant: "danger",
                    loading: actionLoading,
                    disabled: actionLoading,
                }}
                iconClass={"bg-[#FEF0C7]"}
                disableOutsideClose={actionLoading}
                showClose={!actionLoading}
            />

            {/* Declined acknowledgement */}
            <AppModal
                isOpen={declinedOpen}
                onClose={() => setDeclinedOpen(false)}
                icon={<SuccessIcon />}
                title="You’ve declined the domiciliation request"
                body={
                    <>
                        Your decision has been recorded.
                        <br />
                        The financing request may not be approved.
                    </>
                }
                primary={{
                    label: "Understood",
                    onClick: () => {
                        setDeclinedOpen(false);
                        setTimeout(() => {
                            window.location.replace("https://tradevu.co");
                        }, 150);
                    },
                }}
            />
        </div>
    );
};

export default PreviewDomiciliation;
